/**
 * LLM Configuration Service
 *
 * Handles API calls for managing organization-level LLM API keys and budget settings.
 */

import { api } from './api'

export interface LLMConfig {
  id: number
  organization_id: number
  has_anthropic_key: boolean
  has_openai_key: boolean
  has_google_key: boolean
  default_provider: 'anthropic' | 'openai' | 'google'
  daily_budget_usd: number
  monthly_budget_usd: number
  current_day_spend_usd: number
  current_month_spend_usd: number
  is_active: boolean
  budget_exceeded_action: 'pause' | 'notify'
}

export interface SpendLogEntry {
  spend_date: string
  total_spend_usd: number
  anthropic_spend_usd: number
  openai_spend_usd: number
  google_spend_usd: number
  total_input_tokens: number
  total_output_tokens: number
  total_requests: number
  successful_requests: number
  failed_requests: number
}

export interface TestApiKeyResponse {
  valid: boolean
  message: string
  provider: string
}

class LLMConfigService {
  /**
   * Get LLM configuration for an organization
   */
  async getConfig(organizationId: number): Promise<LLMConfig> {
    const response = await api.get(`/llm-config/${organizationId}`)
    return response.data
  }

  /**
   * Set an API key for a provider
   */
  async setApiKey(
    organizationId: number,
    provider: 'anthropic' | 'openai' | 'google',
    apiKey: string
  ): Promise<{ status: string; message: string }> {
    const response = await api.post(`/llm-config/${organizationId}/api-key`, {
      provider,
      api_key: apiKey,
    })
    return response.data
  }

  /**
   * Delete an API key for a provider
   */
  async deleteApiKey(
    organizationId: number,
    provider: 'anthropic' | 'openai' | 'google'
  ): Promise<{ status: string; message: string }> {
    const response = await api.delete(
      `/llm-config/${organizationId}/api-key/${provider}`
    )
    return response.data
  }

  /**
   * Test an API key before saving
   */
  async testApiKey(
    organizationId: number,
    provider: 'anthropic' | 'openai' | 'google',
    apiKey: string
  ): Promise<TestApiKeyResponse> {
    const response = await api.post(
      `/llm-config/${organizationId}/test-api-key`,
      {
        provider,
        api_key: apiKey,
      }
    )
    return response.data
  }

  /**
   * Update budget settings
   */
  async updateBudget(
    organizationId: number,
    dailyBudget?: number,
    monthlyBudget?: number
  ): Promise<{ status: string; message: string }> {
    const response = await api.put(`/llm-config/${organizationId}/budget`, {
      daily_budget_usd: dailyBudget,
      monthly_budget_usd: monthlyBudget,
    })
    return response.data
  }

  /**
   * Update LLM settings
   */
  async updateSettings(
    organizationId: number,
    settings: {
      default_provider?: 'anthropic' | 'openai' | 'google'
      is_active?: boolean
      budget_exceeded_action?: 'pause' | 'notify'
    }
  ): Promise<{ status: string; message: string }> {
    const response = await api.put(`/llm-config/${organizationId}/settings`, settings)
    return response.data
  }

  /**
   * Get spend history
   */
  async getSpendHistory(
    organizationId: number,
    days: number = 30
  ): Promise<SpendLogEntry[]> {
    const response = await api.get(
      `/llm-config/${organizationId}/spend?days=${days}`
    )
    return response.data
  }
}

export const llmConfigService = new LLMConfigService()
